'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FilterBarProps {
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  strategy: string;
  timeframe: string;
  clientType: string;
  searchQuery: string;
}

const FilterBar = ({ onFilterChange }: FilterBarProps) => {
  const { t } = useLanguage();
  const [filters, setFilters] = useState<FilterState>({
    strategy: 'all',
    timeframe: 'all',
    clientType: 'all',
    searchQuery: '',
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const strategies = [
    { value: 'all', label: t('filterBar.allStrategies') },
    { value: 'growth', label: t('filterBar.growthEquity') },
    { value: 'value', label: t('filterBar.valueInvesting') },
    { value: 'dividend', label: t('filterBar.dividendIncome') },
    { value: 'balanced', label: t('filterBar.balancedPortfolio') },
  ];

  const timeframes = [
    { value: 'all', label: t('filterBar.allTime') },
    { value: 'week', label: t('filterBar.thisWeek') },
    { value: 'month', label: t('filterBar.thisMonth') },
    { value: 'quarter', label: t('filterBar.thisQuarter') },
    { value: 'year', label: t('filterBar.thisYear') },
  ];

  const clientTypes = [
    { value: 'all', label: t('filterBar.allClients') },
    { value: 'professional', label: t('filterBar.professionals') },
    { value: 'family-office', label: t('filterBar.familyOffices') },
    { value: 'entrepreneur', label: t('filterBar.entrepreneurs') },
    { value: 'emerging', label: t('filterBar.emergingHNW') },
  ];

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...filters, searchQuery: e.target.value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const resetFilters: FilterState = {
      strategy: 'all',
      timeframe: 'all',
      clientType: 'all',
      searchQuery: '',
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  const hasActiveFilters = filters.strategy !== 'all' || filters.timeframe !== 'all' || filters.clientType !== 'all' || filters.searchQuery !== '';

  return (
    <div className="bg-card rounded-lg shadow-card p-4 lg:p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              variant="outline"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder={t('filterBar.searchPlaceholder')}
              value={filters.searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 bg-background border border-input rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-center lg:justify-start space-x-2 px-4 py-2.5 bg-muted text-foreground rounded-md text-sm font-medium hover:bg-opacity-80 transition-colors"
        >
          <Icon name="FunnelIcon" size={18} variant="outline" />
          <span>{t('common.filters')}</span>
          <Icon
            name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={16}
            variant="outline"
          />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('filterBar.investmentStrategy')}
              </label>
              <select
                value={filters.strategy}
                onChange={(e) => handleFilterChange('strategy', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                {strategies.map((strategy) => (
                  <option key={strategy.value} value={strategy.value}>
                    {strategy.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('filterBar.timeframe')}
              </label>
              <select
                value={filters.timeframe}
                onChange={(e) => handleFilterChange('timeframe', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                {timeframes.map((timeframe) => (
                  <option key={timeframe.value} value={timeframe.value}>
                    {timeframe.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('filterBar.clientType')}
              </label>
              <select
                value={filters.clientType}
                onChange={(e) => handleFilterChange('clientType', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                {clientTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Active filters applied
              </p>
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                <Icon name="XMarkIcon" size={16} variant="outline" />
                <span>Clear All</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;